import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { invalidateReportsCache } from "@/lib/react-query/invalidateReportsCache";
import { invalidatePatientsCache } from "@/lib/react-query/invalidatePatientsCache";
import type { AppointmentInput, AppointmentServiceItemInput } from "@/hooks/useAppointments";
import { generateRecurrenceOccurrences, type RecurrenceFrequency } from "@/lib/recurrence";
import i18n from "@/i18n";

interface RecurrenceCreateInput {
  appointment: Omit<AppointmentInput, "service_items"> & {
    service_items?: AppointmentServiceItemInput[];
  };
  recurrence: {
    frequency: RecurrenceFrequency;
    interval: number;
    weekdays?: number[];
    monthday?: number | null;
    ends_on?: string | null;
    max_occurrences?: number | null;
    timezone?: string;
    window_days?: number;
  };
}

const parseLocalDate = (value: string): Date | null => {
  const [year, month, day] = value.split("-").map((part) => Number(part));
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day, 0, 0, 0, 0);
};

const insertAppointmentItems = async (appointmentId: string, items: AppointmentServiceItemInput[]) => {
  const itemsToInsert = items.filter((item) => item.item_id);
  if (itemsToInsert.length === 0) return;
  const payload = itemsToInsert.map((item) => ({
    appointment_id: appointmentId,
    item_id: item.item_id!,
    qty: item.qty,
    title_snapshot: item.title_snapshot || "",
  }));
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { error } = await (supabase as any).from("appointment_items").insert(payload);
  if (error) throw error;
};

export const useCreateRecurringAppointments = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (input: RecurrenceCreateInput) => {
      const { data: auth } = await supabase.auth.getUser();
      const actor = auth.user?.id;
      if (!actor) throw new Error(i18n.t("calendar:phase8.recurrence.errors.notAuthenticated"));

      const startsAt = new Date(input.appointment.starts_at);
      const endsAt = new Date(input.appointment.ends_at);
      if (Number.isNaN(startsAt.getTime()) || Number.isNaN(endsAt.getTime()) || endsAt <= startsAt) {
        throw new Error(i18n.t("calendar:phase8.recurrence.errors.invalidPeriod"));
      }

      const occurrenceRows = generateRecurrenceOccurrences(startsAt, endsAt, {
        frequency: input.recurrence.frequency,
        interval: input.recurrence.interval,
        weekdays: input.recurrence.weekdays,
        monthday: input.recurrence.monthday,
        endsOn: input.recurrence.ends_on ? parseLocalDate(input.recurrence.ends_on) : null,
        maxOccurrences: input.recurrence.max_occurrences ?? null,
        windowDays: input.recurrence.window_days ?? 90,
      });
      if (occurrenceRows.length === 0) throw new Error(i18n.t("calendar:phase8.recurrence.errors.noOccurrencesGenerated"));

      const recurrencePayload = {
        clinic_id: input.appointment.clinic_id,
        owner_id: input.appointment.owner_id,
        vet_user_id: input.appointment.vet_user_id ?? null,
        type: input.appointment.type,
        notes: input.appointment.notes ?? null,
        is_taxi_dog: input.appointment.is_taxi_dog ?? false,
        taxi_dog_type: input.appointment.taxi_dog_type ?? null,
        timezone: input.recurrence.timezone ?? "America/Sao_Paulo",
        frequency: input.recurrence.frequency,
        interval: input.recurrence.interval,
        weekdays: input.recurrence.frequency === "weekly" ? (input.recurrence.weekdays ?? []) : null,
        monthday: input.recurrence.frequency === "monthly" ? (input.recurrence.monthday ?? startsAt.getDate()) : null,
        starts_on: occurrenceRows[0].occurrenceDate,
        ends_on: input.recurrence.ends_on ?? null,
        max_occurrences: input.recurrence.max_occurrences ?? null,
        active: true,
        created_by_user_id: actor,
      };

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data: recurrence, error: recurrenceError } = await (supabase as any)
        .from("recurrence")
        .insert(recurrencePayload)
        .select("id")
        .single();
      if (recurrenceError) throw recurrenceError;

      const recurrenceId = recurrence.id as string;
      const recurrenceItems = (input.appointment.service_items ?? [])
        .filter((item) => item.item_id)
        .map((item, idx) => ({
          recurrence_id: recurrenceId,
          item_id: item.item_id!,
          qty: item.qty,
          title_snapshot: item.title_snapshot || "",
          sort_order: idx,
        }));
      if (recurrenceItems.length > 0) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { error } = await (supabase as any).from("recurrence_items").insert(recurrenceItems);
        if (error) throw error;
      }

      const appointmentPayload = occurrenceRows.map((occ) => ({
        clinic_id: input.appointment.clinic_id,
        created_by_user_id: actor,
        vet_user_id: input.appointment.vet_user_id ?? null,
        owner_id: input.appointment.owner_id,
        pet_id: input.appointment.pet_ids[0],
        type: input.appointment.type,
        status: input.appointment.status ?? "agendado",
        starts_at: occ.startsAt.toISOString(),
        ends_at: occ.endsAt.toISOString(),
        notes: input.appointment.notes ?? null,
        is_taxi_dog: input.appointment.is_taxi_dog ?? false,
        taxi_dog_type: input.appointment.taxi_dog_type ?? null,
        recurrence_id: recurrenceId,
        recurrence_occurrence_date: occ.occurrenceDate,
        is_recurrence_exception: false,
      }));

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data: appointments, error: appointmentError } = await (supabase as any)
        .from("appointments")
        .insert(appointmentPayload)
        .select("id");
      if (appointmentError) throw appointmentError;

      const appointmentIds = (appointments ?? []).map((row: { id: string }) => row.id);
      const petIds = Array.from(new Set(input.appointment.pet_ids)).filter(Boolean);
      if (petIds.length > 0 && appointmentIds.length > 0) {
        const relations = appointmentIds.flatMap((appointmentId: string) =>
          petIds.map((petId) => ({
            appointment_id: appointmentId,
            pet_id: petId,
          })),
        );
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { error } = await (supabase as any).from("appointment_pets").insert(relations);
        if (error) throw error;
      }

      for (const appointmentId of appointmentIds) {
        await insertAppointmentItems(appointmentId, input.appointment.service_items ?? []);
      }

      return {
        recurrenceId,
        appointmentIds,
        ownerId: input.appointment.owner_id,
        petIds: input.appointment.pet_ids,
      };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["appointments"] });
      queryClient.invalidateQueries({ queryKey: ["appointment", result.appointmentIds[0]] });
      queryClient.invalidateQueries({ queryKey: ["grooming-appointments"] });
      invalidateReportsCache(queryClient);
      invalidatePatientsCache(queryClient, { ownerId: result.ownerId, petIds: result.petIds });
      toast({ title: i18n.t("calendar:phase8.recurrence.createdTitle") });
    },
    onError: (error: Error) => {
      toast({
        title: i18n.t("calendar:phase8.recurrence.createErrorTitle"),
        description: error.message,
        variant: "destructive",
      });
    },
  });
};
